import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, PackageSearch, Truck, CreditCard, ShieldCheck, ShieldOff, CheckCircle2, Weight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import AdminServiceCentres from "@/components/AdminServiceCentres";

export default function Admin() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (auth && !auth.is_owner) { navigate("/app", { replace: true }); return; }
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {});
  }, [auth, navigate]);

  const cards = [
    { label: "Members", value: stats?.total_members, icon: Users },
    { label: "Loads", value: stats?.total_loads, icon: PackageSearch },
    { label: "Trucks", value: stats?.total_trucks, icon: Truck },
    { label: "Active subs", value: stats?.active_subscriptions, icon: CreditCard },
    { label: "Payments", value: stats?.paid_transactions, icon: ShieldCheck },
    { label: "Loads placed", value: stats?.placed_loads, icon: CheckCircle2 },
    { label: "Freight moved (T)", value: stats?.weight_moved, icon: Weight },
  ];

  const toggleVerify = async (m) => {
    try {
      await api.put(`/admin/members/${m.user_id}/verify`, { verified: !m.verified });
      setMembers((prev) => prev.map((x) => x.user_id === m.user_id ? { ...x, verified: !m.verified } : x));
      toast.success(!m.verified ? `${m.name} is now verified` : `Removed verification for ${m.name}`);
    } catch (e) { toast.error("Could not update verification"); }
  };

  const badge = (m) => {
    if (m.role === "admin") return <span className="px-2 py-0.5 rounded-full text-xs bg-primary/15 text-primary border border-primary/30">Owner</span>;
    if (m.subscription_status === "active" && m.has_access) return <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Subscribed</span>;
    if (m.has_access) return <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-300 border border-amber-500/30">Trial</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs bg-secondary text-muted-foreground border border-border">Expired</span>;
  };

  return (
    <div className="max-w-6xl">
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-1">Admin</h1>
      <p className="text-muted-foreground text-sm mb-6">Network overview and member management.</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`} className="rounded-xl border border-border bg-card/60 p-5">
            <c.icon className="w-5 h-5 text-primary mb-3" />
            <div className="font-heading font-extrabold text-3xl font-mono-num">{c.value ?? "—"}</div>
            <div className="text-xs text-muted-foreground mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card/40 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border font-heading font-semibold">Members</div>
        <div className="divide-y divide-border/60">
          {members.map((m) => (
            <div key={m.user_id} data-testid="member-row" className="flex items-center gap-3 px-5 py-3.5">
              <Avatar className="w-9 h-9 border border-border"><AvatarImage src={m.picture} /><AvatarFallback className="text-xs bg-secondary">{m.name?.[0] || "U"}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate flex items-center gap-1.5">
                  {m.name}
                  {m.verified && <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0" title="Verified" />}
                  {m.company && <span className="text-muted-foreground font-normal">· {m.company}</span>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
              </div>
              {badge(m)}
              {m.role !== "admin" && (
                <Button data-testid="verify-member-btn" size="sm" variant={m.verified ? "secondary" : "outline"}
                        onClick={() => toggleVerify(m)} className="rounded-full h-7 text-xs shrink-0">
                  {m.verified ? <><ShieldOff className="w-3.5 h-3.5 mr-1" />Unverify</> : <><ShieldCheck className="w-3.5 h-3.5 mr-1" />Verify</>}
                </Button>
              )}
            </div>
          ))}
          {members.length === 0 && <div className="px-5 py-8 text-center text-sm text-muted-foreground">No members yet.</div>}
        </div>
      </div>

      <div className="mt-8">
        <AdminServiceCentres />
      </div>
    </div>
  );
}
